import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronUp, Plus, Loader2 } from 'lucide-react';
import { api } from '../../api/client';
import { Switch } from '../../components/ui/switch';
import { useCanWriteCompanySettings } from '../../hooks/useCanWriteCompanySettings';
import { unwrapArray } from '../../lib/api-envelope';
import {
  compareServiceTypes,
  toServiceType,
  UNSET_DISPLAY_ORDER,
  type ServiceType,
} from '../../types/service-type';
import { Tooltip } from '../../components/ui/tooltip';

/**
 * The per-tenant service catalog — the labels offered on a job's Services
 * Required (`NewJobScreen`).
 *
 * Three things this page must not pretend about:
 *
 *   - There is ONE way to take a service out, and it is Deactivate. DELETE
 *     /service-types/:id is the same soft delete as PATCH isActive:false — it
 *     never removes a row, standard or custom — so a second "Remove" control
 *     presented one action as two. It is gone; the endpoint is untouched.
 *   - A deactivated service drops off this list, because the GET is
 *     active-only unless asked for `include_inactive`, and this page does not
 *     ask. So there is no Activate control to show, and the copy says so
 *     rather than implying one.
 *   - Deactivating or renaming a service does not rewrite the jobs that already
 *     reference it. `jobs.services_required` is jsonb holding display strings,
 *     so an old job keeps the exact label it was saved with and renders it
 *     greyed as "No longer offered". A rename here is not a migration.
 *   - `triggersTorqueSheet` is the tenant's to set, on standard rows as much as
 *     custom ones. Dutch Casing deactivated the seeded "Torque Monitoring" row
 *     and books torque as their own "CTM"; with the flag off, their jobs never
 *     reached Dispatch → Needs PDF and only a database edit could fix it. The
 *     switch sends exactly that one key, so a row's name, order and active
 *     state are never re-sent alongside it.
 *
 * Cross-vertical: no `hasVertical` guard, here or on the nav entry.
 */

const inputCls = 'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm placeholder-fieldiq-muted focus:outline-none focus:border-fieldiq-accent/50';

export default function ServicesPage() {
  const queryClient = useQueryClient();
  // POST, PATCH and DELETE /service-types are requireRole('admin'), non-strict.
  // The GET has no role gate, so the list is shown to everyone.
  const canWrite = useCanWriteCompanySettings();

  const [addName, setAddName] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);

  const {
    data: services = [],
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useQuery<ServiceType[]>({
    queryKey: ['service-types'],
    queryFn: () =>
      api.serviceTypes.list().then((r) =>
        unwrapArray<any>(r.data)
          .map(toServiceType)
          .filter((s): s is ServiceType => s !== null)
          .sort(compareServiceTypes)
      ),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['service-types'] });

  /** A 409 from POST/PATCH means the name is taken — that belongs on the field. */
  const messageFor = (err: any, fallback: string) =>
    err?.response?.status === 409
      ? 'That service already exists'
      : err?.response?.data?.error || fallback;

  const handleAdd = async () => {
    const name = addName.trim();
    if (!name) return;
    setSaving(true);
    setAddError(null);
    try {
      await api.serviceTypes.create({ name });
      setAddName('');
      refresh();
    } catch (err: any) {
      setAddError(messageFor(err, 'Failed to add service'));
    } finally {
      setSaving(false);
    }
  };

  const handleRename = async (id: string) => {
    const name = editName.trim();
    if (!name) return;
    setSaving(true);
    setEditError(null);
    try {
      await api.serviceTypes.update(id, { name });
      setEditId(null);
      refresh();
    } catch (err: any) {
      setEditError(messageFor(err, 'Failed to rename service'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (svc: ServiceType) => {
    // Asked first because, from this page, it cannot be undone: the row leaves
    // the active-only list and there is no Activate to reach it by.
    if (svc.isActive && !window.confirm(
      `Deactivate "${svc.name}"? It comes off the job form and off this list, and can't be reactivated from here yet. Jobs that already list it keep their value.`
    )) return;
    setRowError(null);
    try {
      await api.serviceTypes.update(svc.id, { isActive: !svc.isActive });
      refresh();
    } catch (err: any) {
      setRowError(messageFor(err, 'Failed to update service'));
    }
  };

  const handleTorqueToggle = async (svc: ServiceType) => {
    setRowError(null);
    try {
      await api.serviceTypes.update(svc.id, { triggersTorqueSheet: !svc.triggersTorqueSheet });
      refresh();
    } catch (err: any) {
      setRowError(messageFor(err, 'Failed to update torque sheet setting'));
    }
  };

  /**
   * Move-up: two PATCHes, one per swapped row. Nothing else is renumbered.
   *
   * Two guards on the numbers themselves. A row that arrived with no
   * `displayOrder` carries the local sort sentinel, which must never be sent
   * back — its list position stands in. And when the pair already shares an
   * order (the list breaks that tie by name), swapping equal values would be a
   * no-op, so the mover takes `order - 1` instead.
   */
  const handleMoveUp = async (idx: number) => {
    if (idx === 0) return;
    const above = services[idx - 1];
    const mover = services[idx];
    const orderOf = (s: ServiceType, i: number) =>
      s.displayOrder === UNSET_DISPLAY_ORDER ? i : s.displayOrder;
    const aboveOrder = orderOf(above, idx - 1);
    const moverOrder = orderOf(mover, idx);
    const [nextMover, nextAbove] =
      aboveOrder === moverOrder ? [aboveOrder - 1, aboveOrder] : [aboveOrder, moverOrder];

    setRowError(null);
    try {
      await Promise.all([
        api.serviceTypes.update(mover.id, { displayOrder: nextMover }),
        api.serviceTypes.update(above.id, { displayOrder: nextAbove }),
      ]);
    } catch (err: any) {
      setRowError(messageFor(err, 'Failed to reorder'));
    } finally {
      // Re-read either way: one PATCH may have landed and the other not.
      refresh();
    }
  };

  return (
    <div className="min-h-full">
      {/* The Operations workspace renders the title and the tab strip. */}
      <div className="px-6 py-4 space-y-3">
        <p className="text-xs text-fieldiq-muted">
          The services offered on a job. Active ones appear as checkboxes on new and edited jobs.
          Deactivating one takes it off that list and off this page; jobs that already list it keep
          their value. It can't be reactivated from here yet.
        </p>

        {!canWrite && (
          <p className="text-xs text-fieldiq-muted">Only an admin can add, rename, reorder, or deactivate services.</p>
        )}

        <p className="text-xs text-fieldiq-muted">
          <span className="font-medium text-fieldiq-text">Needs torque sheet PDF</span> — jobs booked
          with that service appear in Dispatch → Needs PDF until a torque sheet is attached.
        </p>

        {rowError && <p className="text-xs text-red-400">{rowError}</p>}

        {isLoading && (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" />
          </div>
        )}

        {isError && (
          <div className="bg-fieldiq-card border border-amber-500/40 rounded-xl p-4 flex items-center justify-between gap-3">
            <p className="text-sm text-amber-300">Couldn't load services.</p>
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-amber-500/50 text-amber-300 hover:text-white disabled:opacity-50"
            >
              {isFetching ? 'Retrying…' : 'Retry'}
            </button>
          </div>
        )}

        {!isLoading && !isError && services.length === 0 && (
          <div className="text-center py-12 text-fieldiq-muted text-sm">No services yet</div>
        )}

        {services.map((svc, idx) => (
          <div
            key={svc.id}
            className={`bg-fieldiq-card border border-fieldiq-border rounded-xl p-4 flex items-center gap-3 ${svc.isActive ? '' : 'opacity-50'}`}
          >
            {canWrite && (
            <Tooltip content="Move up">
              <button
                onClick={() => handleMoveUp(idx)}
                disabled={idx === 0}
                className="text-fieldiq-text hover:text-white disabled:opacity-30 disabled:hover:text-fieldiq-text"
                aria-label={`Move ${svc.name} up`}
              >
                <ChevronUp size={16} />
              </button>
            </Tooltip>
            )}

            {editId === svc.id ? (
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <input
                    value={editName}
                    onChange={(e) => { setEditName(e.target.value); setEditError(null); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleRename(svc.id); }}
                    className={`${inputCls} flex-1 ${editError ? 'border-red-500' : ''}`}
                    placeholder="Service name"
                    aria-label="Service name"
                    autoFocus
                  />
                  <button
                    onClick={() => handleRename(svc.id)}
                    disabled={saving || !editName.trim()}
                    className="px-3 py-1.5 text-xs font-semibold bg-fieldiq-accent text-white rounded-lg disabled:opacity-50"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => { setEditId(null); setEditError(null); }}
                    className="px-3 py-1.5 text-xs text-fieldiq-text"
                  >
                    Cancel
                  </button>
                </div>
                {editError && <p className="text-xs text-red-400 mt-1.5">{editError}</p>}
              </div>
            ) : (
              <>
                <span className="text-sm font-medium text-white flex-1">{svc.name}</span>
                {svc.isStandard && (
                  <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-fieldiq-border text-fieldiq-muted">
                    Standard
                  </span>
                )}
                {/* On standard rows too: the tenant owns the fact, whichever row
                    they book torque under. */}
                <span className="flex items-center gap-2 text-xs text-fieldiq-muted whitespace-nowrap">
                  Needs torque sheet PDF
                  <Switch
                    checked={svc.triggersTorqueSheet}
                    onCheckedChange={() => handleTorqueToggle(svc)}
                    disabled={!canWrite}
                    aria-label={`Needs torque sheet PDF for ${svc.name}`}
                    title="Jobs with this service appear in Dispatch → Needs PDF"
                  />
                </span>
                {canWrite && (
                  <>
                    <button
                      onClick={() => { setEditId(svc.id); setEditName(svc.name); setEditError(null); }}
                      className="px-2 py-1 text-xs text-fieldiq-accent hover:text-white"
                      aria-label={`Rename ${svc.name}`}
                    >
                      Rename
                    </button>
                    {/* The one way out. The list is active-only, so every row
                        here is active and there is no Activate branch to draw. */}
                    <button
                      onClick={() => handleToggle(svc)}
                      className="px-2 py-1 text-xs text-amber-400"
                      aria-label={`Deactivate ${svc.name}`}
                    >
                      Deactivate
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        ))}

        {/* Add new — admin only, matching POST /service-types. */}
        {canWrite && (
        <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4">
          <p className="text-sm font-medium text-white mb-3">Add Service</p>
          <div className="flex items-center gap-2">
            <input
              value={addName}
              onChange={(e) => { setAddName(e.target.value); setAddError(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
              className={`${inputCls} flex-1 ${addError ? 'border-red-500' : ''}`}
              placeholder="Service name"
              aria-label="New service name"
            />
            <button
              onClick={handleAdd}
              disabled={saving || !addName.trim()}
              className="px-4 py-2 text-xs font-semibold bg-fieldiq-accent text-white rounded-lg disabled:opacity-50 flex items-center gap-1"
            >
              <Plus size={14} /> Add
            </button>
          </div>
          {addError && <p className="text-xs text-red-400 mt-1.5">{addError}</p>}
        </div>
        )}
      </div>
    </div>
  );
}
